import React from 'react';
import { OrdemDeServicoCompleta } from '../../types/ordemServico';
import { Truck, UserCheck, Tag, DollarSign, AlertCircle } from 'lucide-react';
import { MaskedValue } from '../common/ProtectedField';
import { fixEncodingIssues } from '../../utils/textUtils';

interface OsLogisticaVeiculoSectionProps {
  os: OrdemDeServicoCompleta;
}

export const OsLogisticaVeiculoSection: React.FC<OsLogisticaVeiculoSectionProps> = ({ os }) => {
  const formatCurrency = (val?: number) => {
    const num = val || 0;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const tipoVeiculo = os.tipo_veiculo?.tipo_veiculo || `Tipo #${os.tipo_veiculo_id || '-'}`;
  const motoristaNome = os.veiculos?.motorista?.nome || os.motorista_nome || 'Não alocado';
  const placa = os.veiculos?.placa || os.veiculos_id || 'N/A';
  const servicoNome = fixEncodingIssues(os.fk_item_servico?.tipo_servico?.tipo || os.fk_item_servico?.servicos || 'Serviço Padrão');
  const ajudanteNome = fixEncodingIssues(os.fk_item_ajudante?.servicos || (os.quantidade_ajudante ? `${os.quantidade_ajudante} Ajudante(s)` : 'Sem ajudante'));

  return (
    <div className="space-y-4 pt-4 border-t border-slate-200">
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
        <Truck className="w-4 h-4 text-red-600" />
        Veículo, Logística & Valores
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Tipo Veículo */}
        <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl">
          <span className="text-xs text-slate-400 font-semibold block uppercase">Tipo de Veículo</span>
          <span className="text-sm font-bold text-slate-900">{tipoVeiculo}</span>
        </div>

        {/* Motorista */}
        <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl">
          <span className="text-xs text-slate-400 font-semibold block uppercase flex items-center gap-1">
            <UserCheck className="w-3.5 h-3.5" /> Motorista
          </span>
          <span className="text-sm font-bold text-slate-900">{motoristaNome}</span>
        </div>

        {/* Placa */}
        <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl">
          <span className="text-xs text-slate-400 font-semibold block uppercase">Placa</span>
          <span className="text-sm font-bold text-slate-900 tracking-wider font-mono">{placa}</span>
        </div>
      </div>

      {/* Serviços e Ajudante Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Veículo / Serviço */}
        <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl flex justify-between items-center">
          <div>
            <span className="text-xs text-slate-400 font-semibold block uppercase flex items-center gap-1">
              <Tag className="w-3.5 h-3.5" /> Serviço / Veículo
            </span>
            <p className="text-sm font-bold text-slate-900 mt-0.5">{servicoNome}</p>
          </div>
          <div className="text-right">
            <span className="text-xs text-slate-400 font-semibold block uppercase">Valor Unit.</span>
            <p className="text-sm font-bold text-slate-900 mt-0.5">
              <MaskedValue
                value={os.valor_unit_servi}
                permission="view_order_prices"
                type="currency"
              />
            </p>
          </div>
        </div>

        {/* Ajudante */}
        <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl flex justify-between items-center">
          <div>
            <span className="text-xs text-slate-400 font-semibold block uppercase">Ajudante (Qtd: {os.quantidade_ajudante || 0})</span>
            <p className="text-sm font-bold text-slate-900 mt-0.5">{ajudanteNome}</p>
          </div>
          <div className="text-right">
            <span className="text-xs text-slate-400 font-semibold block uppercase">Valor Unit.</span>
            <p className="text-sm font-bold text-slate-900 mt-0.5">
              <MaskedValue
                value={os.valor_unit_ajud}
                permission="view_order_repasse"
                type="currency"
              />
            </p>
          </div>
        </div>
      </div>

      {/* Sub Total Viagem */}
      <div className="p-3.5 bg-red-50/60 border border-red-100 rounded-xl flex justify-between items-center">
        <span className="text-xs font-bold text-red-700 uppercase flex items-center gap-1">
          <DollarSign className="w-4 h-4" /> Sub Total Viagem
        </span>
        <span className="text-base font-bold text-red-700">
          <MaskedValue
            value={os.subtotal_viagem}
            permission="view_order_prices"
            type="currency"
          />
        </span>
      </div>

      {/* Ocorrências */}
      <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl">
        <span className="text-xs text-slate-400 font-semibold block uppercase flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5 text-amber-500" /> Ocorrências
        </span>
        <p className="text-sm text-slate-800 mt-1">{os.ocorrencias || 'Nenhuma ocorrência registrada.'}</p>
      </div>
    </div>
  );
};
